import React, { useContext, useEffect, useState } from 'react';
import {
  TableCell,
  TableRow,
  TableBody,
  Table,
  TableContainer,
  Button,
  Typography,
  TableHead,
  Stack,
  Box,
  TablePagination,
  Card,
  Drawer,
  Chip,
  ClickAwayListener,
  Tooltip,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import SnackbarInfo from '../common/snackbar-info.component';
import Channels from '../form-elements/channels.component';
import {
  channelVideoSchedule,
  getChannelVideoScheduleList,
  updateVideoSchedule,
} from '../../services/videos.service';
import { getChannelListByGenreId } from '../../services/channel-genres.service';
import ChannelGenres from '../form-elements/channel-genres.component';
import Loading from '../table-elements/loading.component';
import { FiltersContext } from '../../context/filter.context';
import FilterVideos from '../filter-videos/filter-video.component';
import HTMLDatepicker from '../form-elements/html-datepicker.component';
import ConfirmPopup from '../common/confirm-popup.component';
import Iconify from '../common/iconify.component';
import { formatDate, getCurrentTimestamp } from '../../utils/datetime.util';
import useAccess from '../../hooks/access.hook';
import {
  getLocalFilterValue,
  setLocalFilterValue,
} from '../../utils/local-cache.util';
import { VIEW_TOOLTIP_DATA } from '../../config/module-configs/content-videos.config';
import CellValue from '../table-elements/cell-value.component';
import ROUTE_PATH from '../../config/routes.config';
import replaceIdUrl from '../../utils/url.util';
import DistributorFilterVideos from '../filter-videos/distributor-filter-videos.component';
import { getSearchListData } from '../../services/apps.service';
import useAbortableRequest from '../../hooks/abortable-request.hook';

const ChannelAssignment = () => {
  const FILTERS_KEY = 'channel-assignment-filters';

  const { getSignal, isAbortError } = useAbortableRequest();

  const { hasOperationAccess, hasContentOwnerAccess } = useAccess();

  const { control, setValue, getValues } = useForm({});

  const { openFilter, handleToggleFilters } = useContext(FiltersContext);
  const [channelsDetail, setChannelsDetail] = useState({
    rows: [],
    selectedChannelGenre: { id: null, name: 'None' },
    selectedChannel: { id: 'all', title: 'All Channels', code: null },
  });
  const [options, setOptions] = useState({
    page: 0,
    rowsPerPage: 100,
    videoTotalRows: 0,
    videoRows: [],
    reloadCounter: 0,
    appliedFilters: {},
    loading: false,
    scheduleData: {},
  });
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [selectedAction, setSelectedAction] = useState({
    action: null,
    selectedVideo: null,
    selectedChannel: null,
    selectedVideoId: null,
    selectedChannelId: null,
  });
  const [clickedRowData, setClickedRowData] = useState({});
  const [showDatepicker, setShowDatepicker] = useState([]);

  const handleShowTooltip = (r) => {
    if (r?.id === clickedRowData?.id) {
      setClickedRowData({});
    } else {
      setClickedRowData(r);
    }
  };

  const handlePageChange = (v) => {
    setOptions({
      ...options,
      page: v,
      loading: true,
      reloadCounter: options.reloadCounter + 1,
    });
  };
  const handleRowsPerPageChange = (v) => {
    setOptions({
      ...options,
      rowsPerPage: v,
      page: 0,
      loading: true,
      reloadCounter: options.reloadCounter + 1,
    });
  };

  const handleApplyFilters = (selectedFilters) => {
    setLocalFilterValue(FILTERS_KEY, selectedFilters);
    const counter =
      Object.values(selectedFilters).filter((filter) => filter.apply).length ||
      0;

    setOptions({
      ...options,
      appliedFilters: { ...selectedFilters },
      filtersCounter: counter,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleClearFilters = () => {
    setLocalFilterValue(FILTERS_KEY, {});
    setOptions({
      ...options,
      appliedFilters: {},
      filtersCounter: 0,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleDeleteFilter = (field) => {
    if (field === 'clear-all') {
      handleClearFilters();
      return;
    }

    const updatedFilters = { ...options.appliedFilters };
    if (field === 'licenese_expired_videos') {
      updatedFilters[field] = { ...updatedFilters[field], value: false };
    } else {
      delete updatedFilters[field];
    }
    setLocalFilterValue(FILTERS_KEY, updatedFilters);

    setOptions({
      ...options,
      appliedFilters: updatedFilters,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const fetchVideoData = (id, type) => {
    const signal = getSignal();
    setOptions({ ...options, loading: true });
    const localFilters = getLocalFilterValue(FILTERS_KEY);

    const params = [];
    params.push(`page=${options.page + 1}`);
    params.push(`perPage=${options.rowsPerPage}`);
    if (id && type === 'channelGenreId') {
      params.push(`channelGenreId=${id}`);
    } else if (id && type === 'channelId') {
      params.push(`channelId=${id}`);
    }

    const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
    const appliedFilters =
      Object.keys(localFilters).length > 0
        ? localFilters
        : options.appliedFilters;
    const filters = Object.entries(appliedFilters)
      .filter(([, filter]) => filter.apply)
      .map(([field, filter]) => ({
        field,
        ...filter,
      }));
    getChannelVideoScheduleList(paramsQuery, { filter: filters }, signal)
      .then((res) => {
        if (!signal.aborted) {
          setOptions({
            ...options,
            loading: false,
            videoTotalRows: res?.data?.videoDetail?.totalRows || 0,
            videoRows: res?.data?.videoDetail?.rows || [],
            scheduleData: res?.data?.channelVideoSchedules || {},
            appliedFilters: appliedFilters || {},
          });
          Object.keys(res.data.channelVideoSchedules).forEach((item) => {
            const fullDate = res.data.channelVideoSchedules[item];

            setValue(item, fullDate);
          });
        }
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        setOptions({
          ...options,
          loading: false,
          videoTotalRows: 0,
          videoRows: [],
        });
      });
  };

  const handleChannelGenreChange = (v) => {
    if (v?.id) {
      getChannelListByGenreId(v.id)
        .then((res) => {
          setChannelsDetail({
            ...channelsDetail,
            rows: res.data,
            selectedChannelGenre: v || {},
            selectedChannel: { id: null, title: 'None', code: null },
          });
        })
        .catch((err) => {
          const message =
            err.response?.data?.message ||
            'Something went wrong, please try again.';

          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'error',
            message,
          });
        });

      fetchVideoData(v.id, 'channelGenreId');
    } else {
      setChannelsDetail({
        ...channelsDetail,
        rows: [],
        selectedChannelGenre: v || {},
        selectedChannel: { id: null, title: 'None', code: null },
      });
    }
  };

  const handleChannelChange = (v) => {
    if (v?.id === 'all') {
      getSearchListData()
        .then((res) => {
          setChannelsDetail({
            ...channelsDetail,
            rows: res.data || [],
            selectedChannel: v || {},
            selectedChannelGenre: { id: null, name: 'None' },
          });
        })
        .catch((err) => {
          const message =
            err.response?.data?.message ||
            'Something went wrong, please try again.';

          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'error',
            message,
          });
        });

      fetchVideoData(null, 'channelId');
    } else {
      setChannelsDetail({
        ...channelsDetail,
        rows: v?.id ? [v] : [],
        selectedChannel: v || {},
        selectedChannelGenre: { id: null, name: 'None' },
      });

      if (v?.id) fetchVideoData(v.id, 'channelId');
    }
  };

  const handleSaveData = (key) => {
    const data = getValues();
    const parts = key.split('_');

    const videoId = parts[0];
    const channelId = parts[1];

    const fromDate = data[`from_${key}`]
      ? dayjs(data[`from_${key}`]).format('YYYY-MM-DD')
      : null;
    const toDate = data[`to_${key}`]
      ? dayjs(data[`to_${key}`]).format('YYYY-MM-DD')
      : null;

    const updatedData = {
      scheduleData: [
        {
          videoId,
          channelId,
          from: fromDate,
          to: toDate,
        },
      ],
    };

    updateVideoSchedule(updatedData)
      .then(() => {
        setShowDatepicker((prev) => prev.filter((item) => item !== key));
        setOptions({
          ...options,
          scheduleData: {
            ...options.scheduleData,
            [`from_${key}`]: data[`from_${key}`],
            [`to_${key}`]: data[`to_${key}`],
          },
        });
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
      });
  };

  const handleActionCancel = () => {
    setSelectedAction({
      action: null,
      selectedVideo: null,
      selectedChannel: null,
      selectedVideoId: null,
      selectedChannelId: null,
    });
  };

  const handleAddRemoveVideo = (action, videoId, channelId) => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });
    channelVideoSchedule({
      videoId,
      channelId,
      action,
    })
      .then((res) => {
        if (res.data.success) {
          setOptions({
            ...options,
            scheduleData: {
              ...options.scheduleData,
              [`${videoId}_${channelId}`]: action === 'add',
            },
          });
          handleActionCancel();
          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'success',
            message:
              action === 'add'
                ? 'Video added successfully.'
                : 'Video removed successfully.',
          });
          if (action === 'add') {
            setShowDatepicker((prev) => [...prev, `${videoId}_${channelId}`]);
          } else if (action === 'remove') {
            setOptions({
              ...options,
              scheduleData: {
                ...options.scheduleData,
                [`${videoId}_${channelId}`]: false,
                [`from_${videoId}_${channelId}`]: null,
                [`to_${videoId}_${channelId}`]: null,
              },
            });
            setValue(`from_${videoId}_${channelId}`, null);
            setValue(`to_${videoId}_${channelId}`, null);
          }
        }
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';
        handleActionCancel();
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
      });
  };
  const handleRefreshData = () => {
    setOptions({
      ...options,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  useEffect(() => {
    if (options.reloadCounter) {
      setShowDatepicker([]);
      fetchVideoData(
        (channelsDetail.selectedChannel?.id &&
          (channelsDetail.selectedChannel.id === 'all'
            ? null
            : channelsDetail.selectedChannel.id)) ||
          channelsDetail.selectedChannelGenre.id,
        channelsDetail.selectedChannel.id ? 'channelId' : 'channelGenreId'
      );
    }
  }, [options.reloadCounter]);

  useEffect(() => {
    handleChannelChange({ id: 'all', title: 'All Channels', code: null });
  }, []);

  return (
    <Box
      sx={{
        transition: 'margin 0.3s ease, width 0.3s ease',
        width: openFilter ? 'calc(100% - 300px)' : '100%',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: 0, py: 2 }}
      >
        <Box display="flex" alignItems="center" width="50%" gap={1}>
          <ChannelGenres
            id="channel_genres"
            name="channel_genres"
            label="Select Channel Genre"
            defaultValue={channelsDetail.selectedChannelGenre}
            onChange={(v) => handleChannelGenreChange(v)}
            sx={{ width: '100%' }}
          />
          OR
          <Channels
            id="channels"
            name="channels"
            label="Select Channel"
            defaultValue={channelsDetail.selectedChannel}
            onChange={(v) => handleChannelChange(v)}
            sx={{ width: '100%' }}
            showNoneOption
            allOption
          />
        </Box>
        <Box>
          <Button
            color="inherit"
            variant="contained"
            startIcon={<Iconify icon="ic:twotone-refresh" />}
            onClick={handleRefreshData}
            disabled={options.loading}
          >
            Refresh
          </Button>
        </Box>
      </Stack>

      {options.appliedFilters &&
        Object.values(options.appliedFilters).some(
          (filter) =>
            filter.apply &&
            ((filter.type === 'checkbox' && filter.value) ||
              filter.type !== 'checkbox')
        ) && (
          <Box mb={2}>
            <Typography variant="body1" fontWeight="bold" display="inline">
              Filters Applied:{' '}
              <Chip
                key="all"
                label="Clear all"
                onDelete={() => handleDeleteFilter('clear-all')}
                sx={{ ml: 1, color: 'red' }}
              />
            </Typography>

            {Object.entries(options.appliedFilters)
              .filter(
                ([, filter]) =>
                  filter.apply &&
                  ((filter.type === 'checkbox' && filter.value) ||
                    filter.type !== 'checkbox')
              )
              .map(([field, filter]) => (
                <Chip
                  key={field}
                  label={filter.title || 'Active'}
                  onDelete={() => handleDeleteFilter(field)}
                  sx={{ ml: 1 }}
                />
              ))}
          </Box>
        )}

      <Card>
        <form id="add-edit-form">
          {options.videoTotalRows > 0 && channelsDetail.rows.length > 0 && (
            <TablePagination
              component="div"
              count={options.videoTotalRows}
              page={options.page}
              onPageChange={(e, newPage) => handlePageChange(newPage)}
              rowsPerPage={options.rowsPerPage}
              onRowsPerPageChange={(e) => {
                handleRowsPerPageChange(Number(e.target.value));
              }}
            />
          )}

          <TableContainer sx={{ maxHeight: 680, overflowY: 'auto' }}>
            {options.loading && <Loading />}

            {!options.loading && !channelsDetail.rows.length && (
              <Table stickyHeader>
                <TableRow>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ my: 20 }}>
                      {options.videoRows.length > 0 &&
                      channelsDetail.selectedChannelGenre.id
                        ? 'No assigned channel found for selected channel genre'
                        : 'Select channel genre or channel'}
                    </Typography>
                  </TableCell>
                </TableRow>
              </Table>
            )}

            {!options.loading && channelsDetail.rows.length > 0 && (
              <Table stickyHeader sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      width={180}
                      sx={{
                        position: 'sticky',
                        left: 0,
                        top: 0,
                        zIndex: 3,
                      }}
                    >
                      <Typography variant="subtitle2">Video</Typography>
                    </TableCell>
                    <TableCell
                      width={120}
                      sx={{
                        position: 'sticky',
                        left: 180,
                        top: 0,
                        zIndex: 3,
                      }}
                    >
                      <Typography variant="subtitle2">Series</Typography>
                    </TableCell>
                    <TableCell
                      width={100}
                      sx={{
                        position: 'sticky',
                        left: 300,
                        top: 0,
                        zIndex: 3,
                      }}
                    >
                      <Typography variant="subtitle2">S#E#</Typography>
                    </TableCell>
                    {options.videoRows.length > 0 &&
                      channelsDetail.rows?.map((c) => (
                        <TableCell
                          key={`channel-${c.id}`}
                          width={210}
                          sx={{ zIndex: 1, textAlign: 'center' }}
                        >
                          <Typography variant="subtitle2" minWidth="200px">
                            {c.title}
                          </Typography>
                        </TableCell>
                      ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {!options.videoRows.length && (
                    <TableRow>
                      <TableCell align="center" colSpan={3}>
                        <Typography variant="body2" sx={{ my: 20 }}>
                          No video Found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}

                  {options.videoRows.length > 0 &&
                    options.videoRows.map((v) => {
                      let showVideoAlert = false;
                      if (
                        v.offline === 'YES' ||
                        v.exclude_from_db === 'YES' ||
                        v.quality_video === 'FA' ||
                        v.poster_16x9_status === 'OC' ||
                        new Date(v.licenseExpiration).getTime() / 1000 <
                          getCurrentTimestamp()
                      ) {
                        showVideoAlert = true;
                      }

                      return (
                        <TableRow
                          key={`row-${v.id}`}
                          sx={{ backgroundColor: 'white' }}
                        >
                          <TableCell
                            sx={{
                              position: 'sticky',
                              left: 0,
                              zIndex: 2,
                              backgroundColor: 'inherit',
                            }}
                          >
                            {clickedRowData?.id === v?.id ? (
                              <ClickAwayListener
                                onClickAway={() => handleShowTooltip(v)}
                              >
                                <Box>
                                  <Tooltip
                                    open
                                    arrow
                                    title={
                                      <TableContainer>
                                        <Table>
                                          <TableBody>
                                            {VIEW_TOOLTIP_DATA.map((c) => (
                                              <TableRow key={`col-${c.id}`}>
                                                <TableCell
                                                  width="30%"
                                                  variant="head"
                                                  sx={{
                                                    border: 0,
                                                    verticalAlign: 'top',
                                                    py: '4px',
                                                    fontSize: 10,
                                                  }}
                                                >
                                                  {c.label}
                                                </TableCell>
                                                <TableCell
                                                  width="70%"
                                                  variant="body"
                                                  sx={{
                                                    border: 0,
                                                    verticalAlign: 'center',
                                                    py: '4px',
                                                    fontSize: 10,
                                                  }}
                                                >
                                                  <CellValue
                                                    type={c.type}
                                                    dataKey={c.dataKey}
                                                    value={v[c.dataKey]}
                                                  />
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>

                                        {!hasContentOwnerAccess() ? (
                                          <Box
                                            display="flex"
                                            justifyContent="flex-end"
                                          >
                                            <Button
                                              size="small"
                                              color="inherit"
                                              variant="contained"
                                              startIcon={
                                                <Iconify icon="bx:edit" />
                                              }
                                              onClick={() =>
                                                window.open(
                                                  replaceIdUrl(
                                                    ROUTE_PATH.VIDEO_EDIT,
                                                    clickedRowData.id
                                                  ),
                                                  '_blank'
                                                )
                                              }
                                              sx={{ fontSize: 12 }}
                                            >
                                              Edit
                                            </Button>
                                          </Box>
                                        ) : (
                                          <Box
                                            display="flex"
                                            justifyContent="flex-end"
                                          >
                                            <Button
                                              size="small"
                                              color="inherit"
                                              variant="contained"
                                              startIcon={
                                                <Iconify icon="lets-icons:view" />
                                              }
                                              onClick={() =>
                                                window.open(
                                                  replaceIdUrl(
                                                    ROUTE_PATH.VIDEO_VIEW_DETAILS,
                                                    clickedRowData.id
                                                  ),
                                                  '_blank'
                                                )
                                              }
                                              sx={{ fontSize: 12 }}
                                            >
                                              View Detail
                                            </Button>
                                          </Box>
                                        )}
                                      </TableContainer>
                                    }
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          bgcolor: 'white',
                                          color: 'black',
                                          maxWidth: '500px',
                                          boxShadow: 10,
                                        },
                                      },
                                      arrow: {
                                        sx: {
                                          color: '#000000',
                                        },
                                      },
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      component="span"
                                      color={showVideoAlert ? 'red' : 'inherit'}
                                      onClick={() => handleShowTooltip(v)}
                                      sx={{ cursor: 'pointer' }}
                                    >
                                      {v.title}
                                    </Typography>
                                  </Tooltip>
                                </Box>
                              </ClickAwayListener>
                            ) : (
                              <Typography
                                variant="body2"
                                component="span"
                                color={showVideoAlert ? 'red' : 'inherit'}
                                onClick={() => handleShowTooltip(v)}
                                sx={{ cursor: 'pointer' }}
                              >
                                {v.title}
                              </Typography>
                            )}
                            {!hasContentOwnerAccess() && (
                              <>
                                <br />
                                <Typography
                                  variant="body2"
                                  component="span"
                                  color="secondary"
                                >
                                  {v.distributor_name || '-'}
                                </Typography>
                              </>
                            )}
                            <br />
                            <Typography
                              variant="body2"
                              component="span"
                              color="secondary"
                            >
                              {formatDate(v.dateCreation || null, 'll')}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              position: 'sticky',
                              left: 180,
                              zIndex: 2,
                              backgroundColor: 'inherit',
                            }}
                          >
                            {v.series_name}
                          </TableCell>
                          <TableCell
                            sx={{
                              position: 'sticky',
                              left: 300,
                              zIndex: 2,
                              backgroundColor: 'inherit',
                            }}
                          >
                            {v.season && v.episodeNum
                              ? `S${v.season}E${v.episodeNum}`
                              : ''}
                          </TableCell>
                          {channelsDetail.rows?.map((c) => (
                            <TableCell
                              key={`${v.id}-${c.id}`}
                              sx={{
                                zIndex: 1,
                                textAlign: 'center',
                                gap: 1,
                              }}
                            >
                              {options.scheduleData[`${v.id}_${c.id}`] ? (
                                <>
                                  {!showDatepicker.includes(
                                    `${v.id}_${c.id}`
                                  ) ? (
                                    <>
                                      <div>
                                        {formatDate(
                                          options.scheduleData[
                                            `from_${v.id}_${c.id}`
                                          ]
                                        ) || '-'}
                                      </div>
                                      <div>
                                        {formatDate(
                                          options.scheduleData[
                                            `to_${v.id}_${c.id}`
                                          ]
                                        ) || '-'}
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <Controller
                                        name={`from_${v.id}_${c.id}`}
                                        control={control}
                                        defaultValue=""
                                        render={({
                                          field: { onChange, value },
                                        }) => (
                                          <HTMLDatepicker
                                            id={`from_${v.id}_${c.id}`}
                                            name={`from_${v.id}_${c.id}`}
                                            label=""
                                            defaultValue={formatDate(
                                              value,
                                              'YYYY-MM-DD'
                                            )}
                                            clearable
                                            onChange={onChange}
                                            disabled={
                                              !hasOperationAccess(
                                                'manual_assignment',
                                                'manage'
                                              )
                                            }
                                          />
                                        )}
                                      />
                                      <Controller
                                        name={`to_${v.id}_${c.id}`}
                                        control={control}
                                        defaultValue=""
                                        render={({
                                          field: { onChange, value },
                                        }) => (
                                          <HTMLDatepicker
                                            id={`to_${v.id}_${c.id}`}
                                            name={`to_${v.id}_${c.id}`}
                                            label=""
                                            defaultValue={formatDate(
                                              value,
                                              'YYYY-MM-DD'
                                            )}
                                            clearable
                                            onChange={onChange}
                                            disabled={
                                              !hasOperationAccess(
                                                'manual_assignment',
                                                'manage'
                                              )
                                            }
                                          />
                                        )}
                                      />
                                    </>
                                  )}

                                  {hasOperationAccess(
                                    'manual_assignment',
                                    'manage'
                                  ) && (
                                    <>
                                      <button
                                        type="button"
                                        style={{
                                          background: '#FD0100',
                                          color: '#fff',
                                          border: 0,
                                          outline: 0,
                                          cursor: 'pointer',
                                          fontWeight: 'bold',
                                          fontSize: '0.65rem',
                                          padding: '4px 6px',
                                          marginTop: 6,
                                          borderRadius: '6px',
                                          boxShadow:
                                            '0 8px 16px 0 rgba(253, 1, 0, 0.24)',
                                          fontFamily: 'sans-serif',
                                          marginRight: 2,
                                        }}
                                        onClick={() =>
                                          !showDatepicker.includes(
                                            `${v.id}_${c.id}`
                                          )
                                            ? setShowDatepicker((prev) => [
                                                ...prev,
                                                `${v.id}_${c.id}`,
                                              ])
                                            : handleSaveData(`${v.id}_${c.id}`)
                                        }
                                      >
                                        {!showDatepicker.includes(
                                          `${v.id}_${c.id}`
                                        )
                                          ? 'Edit'
                                          : 'Save'}
                                      </button>

                                      <button
                                        type="button"
                                        style={{
                                          background: '#FD0100',
                                          color: '#fff',
                                          border: 0,
                                          outline: 0,
                                          cursor: 'pointer',
                                          fontWeight: 'bold',
                                          fontSize: '0.65rem',
                                          padding: '4px 6px',
                                          marginTop: 6,
                                          borderRadius: '6px',
                                          boxShadow:
                                            '0 8px 16px 0 rgba(253, 1, 0, 0.24)',
                                          fontFamily: 'sans-serif',
                                        }}
                                        onClick={() => {
                                          setSelectedAction({
                                            action: 'remove',
                                            selectedVideo: v.title,
                                            selectedChannel: c.title,
                                            selectedVideoId: v.id,
                                            selectedChannelId: c.id,
                                          });
                                        }}
                                      >
                                        Remove
                                      </button>
                                    </>
                                  )}
                                </>
                              ) : (
                                hasOperationAccess(
                                  'manual_assignment',
                                  'manage'
                                ) && (
                                  <button
                                    type="button"
                                    style={{
                                      background: '#6B7280',
                                      color: '#fff',
                                      border: 0,
                                      outline: 0,
                                      cursor: 'pointer',
                                      fontWeight: 'bold',
                                      fontSize: '0.65rem',
                                      padding: '4px 6px',
                                      marginTop: 6,
                                      borderRadius: '6px',
                                      boxShadow:
                                        'rgba(107, 114, 128, 0.24) 0px 8px 16px 0px',
                                      fontFamily: 'sans-serif',
                                    }}
                                    onClick={() => {
                                      handleAddRemoveVideo('add', v.id, c.id);
                                    }}
                                  >
                                    Add
                                  </button>
                                )
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            )}
          </TableContainer>

          {options.videoTotalRows > 0 && channelsDetail.rows.length > 0 && (
            <TablePagination
              component="div"
              count={options.videoTotalRows}
              page={options.page}
              onPageChange={(e, newPage) => handlePageChange(newPage)}
              rowsPerPage={options.rowsPerPage}
              onRowsPerPageChange={(e) => {
                handleRowsPerPageChange(Number(e.target.value));
              }}
            />
          )}
        </form>
      </Card>

      <Drawer
        anchor="right"
        open={openFilter}
        variant="persistent"
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          sx: { width: 300, border: 'none', overflow: 'hidden' },
        }}
      >
        {hasContentOwnerAccess() ? (
          <DistributorFilterVideos
            values={options.appliedFilters}
            onCancel={handleToggleFilters}
            onSuccess={handleApplyFilters}
            onClear={handleClearFilters}
          />
        ) : (
          <FilterVideos
            values={options.appliedFilters}
            onCancel={handleToggleFilters}
            onSuccess={handleApplyFilters}
            onClear={handleClearFilters}
          />
        )}
      </Drawer>

      {selectedAction.action === 'remove' && (
        <ConfirmPopup
          title={selectedAction.action === 'add' ? 'Add Video' : 'Remove Video'}
          message={
            selectedAction.action === 'add'
              ? `Are you sure you want to add ${selectedAction.selectedVideo} video for ${selectedAction.selectedChannel} channel?`
              : `Are you sure you want to remove ${selectedAction.selectedVideo} video for ${selectedAction.selectedChannel} channel?`
          }
          onCancel={handleActionCancel}
          onSuccess={() => {
            handleAddRemoveVideo(
              'remove',
              selectedAction.selectedVideoId,
              selectedAction.selectedChannelId
            );
          }}
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Box>
  );
};

export default ChannelAssignment;
